import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import weatherIcon from "../../../../assets/images/weather_icon/S30.png";
import humidityIcon from "../../../../assets/images/weather_icon/humidity.png";
import windspeedIcon from "../../../../assets/images/weather_icon/windspeed.png";
import precipitationIcon from "../../../../assets/images/weather_icon/precipitation.png";
import { palette } from "../../../../assets/styles/Palette";
import { Button } from "../../../../assets/styles/ButtonStyle";
import {
  getWeatherData,
  getWeatherForecastData,
} from "../../../../utils/indexedDB";
import { useAtom } from "jotai";
import {
  AUTODISPLAY_TEXT_DATA,
  AUTODISPLAY_LOCATION_DATA,
  AUTODISPLAY_DESIGN_CONCEPT,
  AUTODISPLAY_TITLE,
  AUTODISPLAY_DISPLAY_WIDTH,
  AUTODISPLAY_DISPLAY_HEIGHT,
} from "../../../AtomStates";
import MoleculeModeFloating from "../molecules/MoleculeModeFloating";
import MoleculeSizeArea from "../molecules/MoleculeSizeArea";

const marqueeAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(calc(-100% - 4em));
  }
`;

const OrganismDisplayViewApiPreview = ({ onClose }) => {
  const [orientation, setOrientation] = useState("portrait");
  const [aspectRatio, setAspectRatio] = useState(
    window.innerWidth / window.innerHeight
  );
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weatherData, setWeatherData] = useState({
    temperature: "25",
    wind: "2.3",
    precipitation: "2",
    humidity: "72",
  });
  const lastWeatherUpdateHourRef = useRef(null);

  // 모드 변경 핸들러
  const handleModeChange = (mode) => {
    console.log("디스플레이 모드 변경:", mode);
    // 여기에 모드 변경 로직 추가 가능
  };

  const [displayWidth] = useAtom(AUTODISPLAY_DISPLAY_WIDTH);
  const [displayHeight] = useAtom(AUTODISPLAY_DISPLAY_HEIGHT);

  const [currentTime, setCurrentTime] = useState(new Date());
  const [weatherForecastData, setWeatherForecastData] = useState({
    maxTemperature: "34",
    minTemperature: "23",
    sky: "1", // 1: 맑음, 3: 구름많음, 4: 흐림
  });
  const [lastTempUpdateDate, setLastTempUpdateDate] = useState(null); // TMX, TMN 마지막 업데이트 날짜
  const [textData] = useAtom(AUTODISPLAY_TEXT_DATA);
  const [locationData] = useAtom(AUTODISPLAY_LOCATION_DATA);
  const [designConcept] = useAtom(AUTODISPLAY_DESIGN_CONCEPT);
  const [title] = useAtom(AUTODISPLAY_TITLE);

  const [isTextOverflowing, setIsTextOverflowing] = useState(false);
  const messageContainerRef = useRef(null);
  const messageTextRef = useRef(null);

  const fetchWeatherData = async () => {
    const now = new Date();
    let base_date_obj = new Date();

    let currentHour = now.getHours();
    let base_time = "";

    // 초단기실황 API는 매시 45분 이후에 데이터가 제공됩니다.
    // 안전하게 45분 미만일 경우 이전 시간의 데이터를 요청합니다.
    if (now.getMinutes() < 45) {
      // 현재 시간이 0시이고 45분 미만이면, 전날 23시 데이터를 요청합니다.
      if (currentHour === 0) {
        base_date_obj.setDate(base_date_obj.getDate() - 1);
        base_time = "2300";
      } else {
        // 0시가 아니면, 현재 시간 -1 시간으로 요청합니다.
        base_time = `${String(currentHour - 1).padStart(2, "0")}00`;
      }
    } else {
      // 45분 이상이면 현재 시간으로 데이터를 요청합니다.
      base_time = `${String(currentHour).padStart(2, "0")}00`;
    }

    const base_date = `${base_date_obj.getFullYear()}${String(
      base_date_obj.getMonth() + 1
    ).padStart(2, "0")}${String(base_date_obj.getDate()).padStart(2, "0")}`;

    const nx = locationData.selectedDistricts.x;
    const ny = locationData.selectedDistricts.y;

    const params = {
      pageNo: 1,
      numOfRows: 10,
      dataType: "JSON",
      base_date: base_date,
      base_time: base_time,
      nx,
      ny,
    };

    try {
      const response = await getWeatherData(params);

      if (response.data.response.header.resultCode === "00") {
        const items = response.data.response.body.items.item;
        const weather = {};
        items.forEach((item) => {
          switch (item.category) {
            case "T1H": // 기온
              weather.temperature = item.obsrValue;
              break;
            case "RN1": // 1시간 강수량
              weather.precipitation = item.obsrValue;
              break;
            case "WSD": // 풍속
              weather.wind = item.obsrValue;
              break;
            case "REH": // 습도
              weather.humidity = item.obsrValue;
              break;
            default:
              break;
          }
        });
        setWeatherData((prevData) => ({ ...prevData, ...weather }));
      } else {
        console.error(
          "Weather API Error:",
          response.data.response.header.resultMsg
        );
      }
    } catch (error) {
      console.error("Failed to fetch weather data:", error);
    }
  };

  // 날씨 예보 API 파라미터 계산 함수
  const calculateForecastParams = (currentTime) => {
    const year = currentTime.getFullYear();
    const month = String(currentTime.getMonth() + 1).padStart(2, "0");
    const day = String(currentTime.getDate()).padStart(2, "0");
    const hour = currentTime.getHours();

    // API 발표 시간 기준
    let baseTime;
    if (hour >= 23) {
      baseTime = "2300";
    } else if (hour >= 20) {
      baseTime = "2000";
    } else if (hour >= 17) {
      baseTime = "1700";
    } else if (hour >= 14) {
      baseTime = "1400";
    } else if (hour >= 11) {
      baseTime = "1100";
    } else if (hour >= 8) {
      baseTime = "0800";
    } else if (hour >= 5) {
      baseTime = "0500";
    } else {
      baseTime = "0200";
    }

    // 새벽 시간대는 전날 데이터 사용
    let baseDate;
    if (hour < 2) {
      const yesterday = new Date(currentTime);
      yesterday.setDate(yesterday.getDate() - 1);
      baseDate = `${yesterday.getFullYear()}${String(
        yesterday.getMonth() + 1
      ).padStart(2, "0")}${String(yesterday.getDate()).padStart(2, "0")}`;
      baseTime = "2300";
    } else {
      baseDate = `${year}${month}${day}`;
    }

    return {
      pageNo: 1,
      base_date: baseDate,
      base_time: baseTime,
    };
  };

  // 폰트 로드 함수
  const loadFonts = () => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Pretendard:wght@400;500;600;700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  };

  // 날씨 예보 데이터 가져오기
  const fetchWeatherForecast = async () => {
    try {
      const now = new Date();
      const params = calculateForecastParams(now);

      const forecastParams = {
        pageNo: params.pageNo,
        numOfRows: 12,
        dataType: "JSON",
        base_date: params.base_date,
        base_time: params.base_time,
        nx: locationData.selectedDistricts.x, // 지역별 X 좌표
        ny: locationData.selectedDistricts.y, // 지역별 Y 좌표
      };

      const response = await getWeatherForecastData(forecastParams);
      console.log(`예보 데이터 (${now.getHours()}시):`, params, response);

      if (response.data.response.header.resultCode === "00") {
        const items = response.data.response.body.items.item;
        const forecast = {};

        items.forEach((item) => {
          switch (item.category) {
            case "SKY": // 하늘 상태
              forecast.sky = item.fcstValue;
              break;
            // TMX, TMN은 별도 함수에서 처리하므로 제거
          }
        });

        setWeatherForecastData((prev) => ({ ...prev, ...forecast }));
      }

      // 임시 데이터 (개발용)
      console.log(`예보 API 파라미터 (${now.getHours()}시):`, params);
    } catch (error) {
      console.error("날씨 예보 데이터 가져오기 실패:", error);
    }
  };

  // TMX, TMN 데이터 가져오기 (매일 02시 기준)
  const fetchTemperatureMinMax = async () => {
    try {
      const now = new Date();
      const currentDate = `${now.getFullYear()}${String(
        now.getMonth() + 1
      ).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;

      // 이미 오늘 업데이트했다면 스킵
      if (lastTempUpdateDate === currentDate) {
        console.log("TMX, TMN 데이터는 이미 오늘 업데이트됨");
        return;
      }

      const tempParams = {
        pageNo: 1,
        numOfRows: 300,
        dataType: "JSON",
        base_date: currentDate,
        base_time: "0200",
        nx: locationData.selectedDistricts.x,
        ny: locationData.selectedDistricts.y,
      };

      const response = await getWeatherForecastData(tempParams);

      if (response.data.response.header.resultCode === "00") {
        const items = response.data.response.body.items.item;
        const tempData = {};

        // 오늘 날짜의 TMX, TMN 찾기
        items.forEach((item) => {
          if (item.fcstDate === currentDate) {
            switch (item.category) {
              case "TMX": // 최고 기온
                tempData.maxTemperature = item.fcstValue;
                break;
              case "TMN": // 최저 기온
                tempData.minTemperature = item.fcstValue;
                break;
            }
          }
        });

        // 데이터가 있으면 업데이트
        if (tempData.maxTemperature || tempData.minTemperature) {
          setWeatherForecastData((prev) => ({ ...prev, ...tempData }));
          setLastTempUpdateDate(currentDate);
          console.log("TMX, TMN 데이터 업데이트 완료:", tempData);
        } else {
          console.log("오늘 날짜의 TMX, TMN 데이터를 찾을 수 없음");
        }
      }
    } catch (error) {
      console.error("TMX, TMN 데이터 가져오기 실패:", error);
    }
  };

  useEffect(() => {
    fetchWeatherData();

    const weatherTimerId = setInterval(() => {
      const now = new Date();
      const currentHour = now.getHours();

      // 매시간 15분에 업데이트
      if (now.getMinutes() === 15) {
        if (lastWeatherUpdateHourRef.current !== currentHour) {
          fetchWeatherData();
          lastWeatherUpdateHourRef.current = currentHour;
        }
      }
    }, 60000); // 1분마다 체크

    const dateTimerId = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000);

    return () => {
      clearInterval(weatherTimerId);
      clearInterval(dateTimerId);
    };
  }, []);

  useEffect(() => {
    // 폰트 로드
    loadFonts();

    const updateOrientation = () => {
      const ratio = window.innerWidth / window.innerHeight;
      setAspectRatio(ratio); // aspectRatio state 업데이트

      // 비율이 너무 비슷할 때(0.9 ~ 1.1) 안정적인 처리
      if (ratio >= 1.15) {
        setOrientation("landscape");
      } else if (ratio <= 0.85) {
        setOrientation("portrait");
      } else {
        // 애매한 비율일 때는 화면 크기 기준으로 판단
        setOrientation(window.innerWidth > 1200 ? "landscape" : "portrait");
      }
    };

    updateOrientation();
    window.addEventListener("resize", updateOrientation);

    // 초기 데이터 가져오기
    fetchWeatherData(); // 실황 날씨 데이터
    fetchWeatherForecast();
    fetchTemperatureMinMax(); // TMX, TMN 초기 로드

    // 매시간 정각에 예보 데이터 업데이트
    const weatherInterval = setInterval(() => {
      const now = new Date();
      const currentHour = now.getHours();

      if (now.getMinutes() === 0) {
        fetchWeatherData(); // 매시간 실황 데이터 업데이트
        fetchWeatherForecast();

        // 매일 02시에 TMX, TMN 업데이트
        if (currentHour === 2) {
          fetchTemperatureMinMax();
        }
      }
    }, 60000); // 1분마다 체크

    return () => {
      window.removeEventListener("resize", updateOrientation);
      clearInterval(weatherInterval);
    };
  }, [locationData]); // path와 locationData가 변경되면 다시 실행

  // 시간 업데이트
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // 60초(1분)마다 업데이트

    // 컴포넌트 마운트 시 즉시 한 번 실행
    setCurrentTime(new Date());

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const checkOverflow = () => {
      const container = messageContainerRef.current;
      const text = messageTextRef.current;
      if (container && text) {
        const isOverflow = text.scrollWidth > container.clientWidth;
        if (isOverflow !== isTextOverflowing) {
          setIsTextOverflowing(isOverflow);
        }
      }
    };

    const timer = setTimeout(checkOverflow, 100);
    window.addEventListener("resize", checkOverflow);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [textData, orientation, aspectRatio, isTextOverflowing]);

  const formatTime = (date) => {
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dayOfWeek = ["일", "월", "화", "수", "목", "금", "토"][date.getDay()];
    return `${year}.${month}.${day} (${dayOfWeek})`;
  };

  // 하늘 상태에 따른 날씨 텍스트
  const getWeatherStatus = (skyCode) => {
    switch (skyCode) {
      case "1":
        return "맑음";
      case "3":
        return "구름 많음";
      case "4":
        return "흐림";
      default:
        return "구름 많음";
    }
  };

  return (
    <StyledMainContainer orientation={orientation} aspectRatio={aspectRatio}>
      {/* 네비게이션바(Header) 추가 */}
      <Header>
        <TitleContainer>
          <TemplateCategory>{designConcept}</TemplateCategory>
          <HeaderTitle>{title || "미리보기"}</HeaderTitle>
        </TitleContainer>
        <Button
          Primary
          Large
          Fill
          onClick={onClose}
          style={{ position: "absolute", right: "32px" }}
        >
          미리보기 종료하기
        </Button>
      </Header>

      {/* 사이즈 영역 */}
      <StyledSizeAreaContainer>
        <MoleculeSizeArea
          title="내 디스플레이"
          width={displayWidth.toString()}
          height={displayHeight.toString()}
        />
      </StyledSizeAreaContainer>

      {/* 기존 내용 */}
      <StyledContentWrapper orientation={orientation} aspectRatio={aspectRatio}>
        <StyledMainContentArea
          orientation={orientation}
          aspectRatio={aspectRatio}
        >
          {/* 섹션 1: 타이틀 섹션 */}
          <StyledTitleSection
            orientation={orientation}
            aspectRatio={aspectRatio}
          >
            <StyledLocationTitle
              orientation={orientation}
              aspectRatio={aspectRatio}
            >
              {locationData?.selectedRegion || "고성군"}{" "}
              {locationData?.selectedDistricts?.district || ""}
            </StyledLocationTitle>
            <StyledDateTimeSection
              orientation={orientation}
              aspectRatio={aspectRatio}
            >
              <StyledTimeText
                orientation={orientation}
                aspectRatio={aspectRatio}
              >
                {formatTime(currentTime)}
              </StyledTimeText>
              <StyledDateText
                orientation={orientation}
                aspectRatio={aspectRatio}
              >
                {formatDate(currentTime)}
              </StyledDateText>
            </StyledDateTimeSection>
          </StyledTitleSection>

          {/* 섹션 2: 날씨 메인 섹션 */}
          <StyledWeatherMainSection
            orientation={orientation}
            aspectRatio={aspectRatio}
          >
            {/* 249:917~360:917 비율에서만 아이콘을 별도 섹션으로 분리 */}
            {aspectRatio <= 0.39 && (
              <StyledWeatherIconOnlySection
                orientation={orientation}
                aspectRatio={aspectRatio}
              >
                <StyledWeatherIcon
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                />
              </StyledWeatherIconOnlySection>
            )}

            {/* 일반 비율에서는 기존 구조, 249:917~360:917에서는 아이콘 제외한 온도 영역 */}
            {aspectRatio > 0.39 ? (
              <StyledWeatherIconSection
                orientation={orientation}
                aspectRatio={aspectRatio}
              >
                <StyledWeatherIcon
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                />
                <StyledTemperatureSection
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                >
                  <StyledTemperatureText
                    orientation={orientation}
                    aspectRatio={aspectRatio}
                  >
                    {weatherData.temperature}°
                  </StyledTemperatureText>
                  <StyledWeatherStatusText orientation={orientation}>
                    {getWeatherStatus(weatherForecastData.sky)}
                  </StyledWeatherStatusText>
                </StyledTemperatureSection>
              </StyledWeatherIconSection>
            ) : (
              <StyledTemperatureOnlySection
                orientation={orientation}
                aspectRatio={aspectRatio}
              >
                <StyledTemperatureText
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                >
                  {weatherData.temperature}°
                </StyledTemperatureText>
                <StyledWeatherStatusText orientation={orientation}>
                  {getWeatherStatus(weatherForecastData.sky)}
                </StyledWeatherStatusText>
              </StyledTemperatureOnlySection>
            )}

            <StyledTempInfoSection
              orientation={orientation}
              aspectRatio={aspectRatio}
            >
              <StyledMinMaxSection orientation={orientation}>
                <StyledMinMaxTempText
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                >
                  최저 {weatherForecastData.minTemperature}°
                </StyledMinMaxTempText>
                <StyledDivider orientation={orientation} />
                <StyledMinMaxTempText
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                >
                  최고 {weatherForecastData.maxTemperature}°
                </StyledMinMaxTempText>
              </StyledMinMaxSection>
              <StyledFeelsLikeText
                orientation={orientation}
                aspectRatio={aspectRatio}
              >
                체감 온도 30°
              </StyledFeelsLikeText>
            </StyledTempInfoSection>
          </StyledWeatherMainSection>

          {/* 섹션 3: 날씨 상세 섹션 */}
          <StyledWeatherDetailsSection
            orientation={orientation}
            aspectRatio={aspectRatio}
          >
            <StyledWeatherDetailCard
              orientation={orientation}
              aspectRatio={aspectRatio}
            >
              <StyledDetailIconSection
                orientation={orientation}
                aspectRatio={aspectRatio}
              >
                <StyledHumidityIcon
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                />
                <StyledWeatherLabelText
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                >
                  습도
                </StyledWeatherLabelText>
              </StyledDetailIconSection>
              <StyledDetailValueSection
                orientation={orientation}
                aspectRatio={aspectRatio}
              >
                <StyledWeatherValueText
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                >
                  {weatherData.humidity}
                </StyledWeatherValueText>
                <StyledWeatherUnitText
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                >
                  %
                </StyledWeatherUnitText>
              </StyledDetailValueSection>
            </StyledWeatherDetailCard>

            <StyledWeatherDetailCard
              orientation={orientation}
              aspectRatio={aspectRatio}
            >
              <StyledDetailIconSection
                orientation={orientation}
                aspectRatio={aspectRatio}
              >
                <StyledWindSpeedIcon
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                />
                <StyledWeatherLabelText
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                >
                  풍속
                </StyledWeatherLabelText>
              </StyledDetailIconSection>
              <StyledDetailValueSection
                orientation={orientation}
                aspectRatio={aspectRatio}
              >
                <StyledWeatherValueText
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                >
                  {weatherData.wind}
                </StyledWeatherValueText>
                <StyledWeatherUnitText
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                >
                  m/s
                </StyledWeatherUnitText>
              </StyledDetailValueSection>
            </StyledWeatherDetailCard>

            <StyledWeatherDetailCard
              orientation={orientation}
              aspectRatio={aspectRatio}
            >
              <StyledDetailIconSection
                orientation={orientation}
                aspectRatio={aspectRatio}
              >
                <StyledRainIcon
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                />
                <StyledWeatherLabelText
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                >
                  강수량
                </StyledWeatherLabelText>
              </StyledDetailIconSection>

              <StyledDetailValueSection
                orientation={orientation}
                aspectRatio={aspectRatio}
              >
                <StyledWeatherValueText
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                >
                  {weatherData.precipitation === "0"
                    ? "0"
                    : weatherData.precipitation}
                </StyledWeatherValueText>
                <StyledWeatherUnitText
                  orientation={orientation}
                  aspectRatio={aspectRatio}
                >
                  mm/h
                </StyledWeatherUnitText>
              </StyledDetailValueSection>
            </StyledWeatherDetailCard>
          </StyledWeatherDetailsSection>
        </StyledMainContentArea>

        {/* 섹션 4: 하단 메시지 섹션 */}
        <StyledBottomMessage
          ref={messageContainerRef}
          orientation={orientation}
          aspectRatio={aspectRatio}
        >
          <StyledMessageText
            ref={messageTextRef}
            orientation={orientation}
            aspectRatio={aspectRatio}
            isOverflowing={isTextOverflowing}
            data-text={
              textData ||
              "실시간 날씨 정보를 확인하세요 · 정확한 기상 데이터 제공"
            }
          >
            {textData ||
              "실시간 날씨 정보를 확인하세요 · 정확한 기상 데이터 제공"}
          </StyledMessageText>
        </StyledBottomMessage>
      </StyledContentWrapper>

      {/* 플로팅 모드 선택 버튼 */}
      {/* <StyledFloatingButtonContainer>
        <MoleculeModeFloating onModeChange={handleModeChange} />
      </StyledFloatingButtonContainer> */}
    </StyledMainContainer>
  );
};

export default OrganismDisplayViewApiPreview;

// Styled Components
const StyledLocationTitle = styled.span`
  color: white;

  font-size: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(4vmin, min(5vw, 4.5vh))";
    }
    // 세로형에서 비율에 따른 조정 - 249:917~1054:917 자연스러운 연결
    if (aspectRatio >= 1.15) {
      // 1055:917 비율 이상 - 기본 크기
      return "min(5vmin, 4vh, 6vw)";
    } else if (aspectRatio >= 1.14) {
      // 1054:917 비율 - 1055:917과 연결
      const ratio = (aspectRatio - 1.14) / (1.15 - 1.14);
      const vminSize = 5.2 + (5 - 5.2) * ratio; // 5.2vmin에서 5vmin으로
      const vhSize = 4.2 + (4 - 4.2) * ratio; // 4.2vh에서 4vh으로
      const vwSize = 6.2 + (6 - 6.2) * ratio; // 6.2vw에서 6vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else if (aspectRatio >= 0.27) {
      // 1054:917 ~ 249:917 비율 사이 - 자연스러운 점진적 증가
      const ratio = (aspectRatio - 0.27) / (1.14 - 0.27);
      // 249:917에서 1.5배 크기, 1054:917에서 기본 크기
      const start249Vmin = 11.25; // 249:917에서 1.5배 크기
      const start249Vh = 9;
      const start249Vw = 13.5;
      const end1054Vmin = 5.2; // 1054:917에서 목표 크기
      const end1054Vh = 4.2;
      const end1054Vw = 6.2;
      const vminSize = start249Vmin + (end1054Vmin - start249Vmin) * ratio;
      const vhSize = start249Vh + (end1054Vh - start249Vh) * ratio;
      const vwSize = start249Vw + (end1054Vw - start249Vw) * ratio;
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else {
      // 249:917 비율 이하 - 1.5배 크기
      return "min(11.25vmin, 9vh, 13.5vw)";
    }
  }};
  font-family: Pretendard;

  font-weight: 800;
  line-height: ${({ orientation }) =>
    orientation === "portrait" ? "1.1" : "1.1"};
  text-align: center;
`;

const StyledTimeText = styled.span`
  color: white;

  font-size: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(6vmin, min(8vw, 7vh))";
    }
    // 세로형에서 비율에 따른 조정
    if (aspectRatio >= 0.675) {
      // 619:917 비율 이상 (가로가 더 긴 경우)
      return "min(8vmin, 7vh, 10vw)";
    } else if (aspectRatio >= 0.55) {
      // 619:917 ~ 505:917 비율 - 완만한 증가 시작
      const ratio = (aspectRatio - 0.55) / (0.675 - 0.55);
      const vminSize = 8.8 + (8 - 8.8) * ratio; // 8.8vmin에서 8vmin으로
      const vhSize = 7.7 + (7 - 7.7) * ratio; // 7.7vh에서 7vh으로
      const vwSize = 11 + (10 - 11) * ratio; // 11vw에서 10vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else if (aspectRatio >= 0.4) {
      // 505:917 ~ 367:917 비율 - 중간 단계
      const ratio = (aspectRatio - 0.4) / (0.55 - 0.4);
      const vminSize = 9.8 + (8.8 - 9.8) * ratio; // 9.8vmin에서 8.8vmin으로
      const vhSize = 8.6 + (7.7 - 8.6) * ratio; // 8.6vh에서 7.7vh으로
      const vwSize = 12.5 + (11 - 12.5) * ratio; // 12.5vw에서 11vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else if (aspectRatio <= 0.27) {
      // 249:917 비율 이하 (매우 세로가 긴 경우) - 최대 크기
      return "min(12vmin, 10.5vh, 15vw)";
    } else {
      // 0.27 ~ 0.4 사이의 완만한 조정
      const ratio = (aspectRatio - 0.27) / (0.4 - 0.27);
      const vminSize = 12 + (9.8 - 12) * ratio; // 12vmin에서 9.8vmin으로
      const vhSize = 10.5 + (8.6 - 10.5) * ratio; // 10.5vh에서 8.6vh으로
      const vwSize = 15 + (12.5 - 15) * ratio; // 15vw에서 12.5vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    }
  }};
  font-family: Pretendard;

  font-weight: 400;
  line-height: ${({ orientation }) =>
    orientation === "portrait" ? "1.1" : "1.1"};
  text-align: center;
`;

const StyledDateText = styled.span`
  color: white;

  font-size: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(2.4vmin, min(3vw, 2.8vh))";
    }
    // 세로형에서 비율에 따른 조정
    if (aspectRatio >= 0.675) {
      // 619:917 비율 이상 (가로가 더 긴 경우)
      return "min(2.8vmin, 2.5vh, 3.5vw)";
    } else if (aspectRatio >= 0.55) {
      // 619:917 ~ 505:917 비율 - 완만한 증가 시작
      const ratio = (aspectRatio - 0.55) / (0.675 - 0.55);
      const vminSize = 3.1 + (2.8 - 3.1) * ratio; // 3.1vmin에서 2.8vmin으로
      const vhSize = 2.7 + (2.5 - 2.7) * ratio; // 2.7vh에서 2.5vh으로
      const vwSize = 3.8 + (3.5 - 3.8) * ratio; // 3.8vw에서 3.5vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else if (aspectRatio >= 0.4) {
      // 505:917 ~ 367:917 비율 - 중간 단계
      const ratio = (aspectRatio - 0.4) / (0.55 - 0.4);
      const vminSize = 3.5 + (3.1 - 3.5) * ratio; // 3.5vmin에서 3.1vmin으로
      const vhSize = 3.1 + (2.7 - 3.1) * ratio; // 3.1vh에서 2.7vh으로
      const vwSize = 4.3 + (3.8 - 4.3) * ratio; // 4.3vw에서 3.8vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else if (aspectRatio <= 0.27) {
      // 249:917 비율 이하 (매우 세로가 긴 경우) - 최대 크기
      return "min(4.2vmin, 3.75vh, 5.25vw)";
    } else {
      // 0.27 ~ 0.4 사이의 완만한 조정
      const ratio = (aspectRatio - 0.27) / (0.4 - 0.27);
      const vminSize = 4.2 + (3.5 - 4.2) * ratio; // 4.2vmin에서 3.5vmin으로
      const vhSize = 3.75 + (3.1 - 3.75) * ratio; // 3.75vh에서 3.1vh으로
      const vwSize = 5.25 + (4.3 - 5.25) * ratio; // 5.25vw에서 4.3vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    }
  }};
  font-family: Pretendard;

  font-weight: 700;
  line-height: ${({ orientation }) =>
    orientation === "portrait" ? "1.1" : "1.1"};
  text-align: center;
  white-space: nowrap;
`;

const StyledTemperatureText = styled.span`
  color: white;

  font-size: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(8vmin, min(12vw, 10vh))";
    }
    // 세로형에서 비율에 따른 조정
    if (aspectRatio >= 0.675) {
      // 619:917 비율 이상 (가로가 더 긴 경우)
      return "min(12vmin, 10vh, 14vw)";
    } else if (aspectRatio >= 0.55) {
      // 619:917 ~ 505:917 비율 - 완만한 증가 시작
      const ratio = (aspectRatio - 0.55) / (0.675 - 0.55);
      const vminSize = 13.2 + (12 - 13.2) * ratio; // 13.2vmin에서 12vmin으로
      const vhSize = 11 + (10 - 11) * ratio; // 11vh에서 10vh으로
      const vwSize = 15.5 + (14 - 15.5) * ratio; // 15.5vw에서 14vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else if (aspectRatio >= 0.4) {
      // 505:917 ~ 367:917 비율 - 중간 단계
      const ratio = (aspectRatio - 0.4) / (0.55 - 0.4);
      const vminSize = 14.7 + (13.2 - 14.7) * ratio; // 14.7vmin에서 13.2vmin으로
      const vhSize = 12.2 + (11 - 12.2) * ratio; // 12.2vh에서 11vh으로
      const vwSize = 17.5 + (15.5 - 17.5) * ratio; // 17.5vw에서 15.5vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else if (aspectRatio <= 0.27) {
      // 249:917 비율 이하 (매우 세로가 긴 경우) - 최대 크기
      return "min(18vmin, 15vh, 21vw)";
    } else {
      // 0.27 ~ 0.4 사이의 완만한 조정
      const ratio = (aspectRatio - 0.27) / (0.4 - 0.27);
      const vminSize = 18 + (14.7 - 18) * ratio; // 18vmin에서 14.7vmin으로
      const vhSize = 15 + (12.2 - 15) * ratio; // 15vh에서 12.2vh으로
      const vwSize = 21 + (17.5 - 21) * ratio; // 21vw에서 17.5vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    }
  }};
  font-family: Poppins;

  font-weight: 700;
  line-height: 1;
`;

const StyledWeatherStatusText = styled.span`
  color: white;

  font-size: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(3.2vmin, min(4.5vw, 4vh))";
    }
    // 세로형에서 비율에 따른 조정 - 249:917~1054:917 자연스러운 연결
    if (aspectRatio >= 1.15) {
      // 1055:917 비율 이상 - 기본 크기
      return "min(4.5vmin, 4vh, 5.5vw)";
    } else if (aspectRatio >= 1.14) {
      // 1054:917 비율 - 1055:917과 동일한 크기
      return "min(4.5vmin, 4vh, 5.5vw)";
    } else if (aspectRatio >= 0.27) {
      // 1054:917 ~ 249:917 비율 사이 - 자연스러운 점진적 증가
      const ratio = (aspectRatio - 0.27) / (1.14 - 0.27);
      // 249:917에서 최대 크기, 1054:917에서 기본 크기로 자연스럽게 감소
      const start249Vmin = 6.75; // 249:917에서 최대 크기
      const start249Vh = 6;
      const start249Vw = 8.25;
      const end1054Vmin = 4.5; // 1054:917에서 기본 크기 (1055:917과 동일)
      const end1054Vh = 4;
      const end1054Vw = 5.5;
      const vminSize = start249Vmin + (end1054Vmin - start249Vmin) * ratio;
      const vhSize = start249Vh + (end1054Vh - start249Vh) * ratio;
      const vwSize = start249Vw + (end1054Vw - start249Vw) * ratio;
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else {
      // 249:917 비율 이하 (매우 세로가 긴 경우) - 최대 크기
      return "min(6.75vmin, 6vh, 8.25vw)";
    }
  }};
  font-family: Pretendard;

  font-weight: 700;
  line-height: ${({ orientation }) =>
    orientation === "portrait" ? "1.2" : "1.1"};
  text-align: center;
`;

const StyledMinMaxTempText = styled.span`
  color: white;

  font-size: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(2.8vmin, min(4vw, 3.5vh))";
    }
    // 세로형에서 비율에 따른 조정
    if (aspectRatio >= 1.15) {
      // 1055:917 비율 이상 - 기본 크기
      return "min(3.8vmin, 3.2vh, 4.5vw)";
    } else if (aspectRatio >= 1.14) {
      // 1054:917 비율 - 1055:917과 동일한 크기 유지
      return "min(3.8vmin, 3.2vh, 4.5vw)";
    } else if (aspectRatio >= 0.675) {
      // 619:917 비율 이상 (가로가 더 긴 경우)
      return "min(3.8vmin, 3.2vh, 4.5vw)";
    } else if (aspectRatio >= 0.55) {
      // 619:917 ~ 505:917 비율 - 완만한 증가 시작
      const ratio = (aspectRatio - 0.55) / (0.675 - 0.55);
      const vminSize = 4.2 + (3.8 - 4.2) * ratio; // 4.2vmin에서 3.8vmin으로
      const vhSize = 3.5 + (3.2 - 3.5) * ratio; // 3.5vh에서 3.2vh으로
      const vwSize = 5 + (4.5 - 5) * ratio; // 5vw에서 4.5vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else if (aspectRatio >= 0.4) {
      // 505:917 ~ 367:917 비율 - 중간 단계
      const ratio = (aspectRatio - 0.4) / (0.55 - 0.4);
      const vminSize = 4.7 + (4.2 - 4.7) * ratio; // 4.7vmin에서 4.2vmin으로
      const vhSize = 3.9 + (3.5 - 3.9) * ratio; // 3.9vh에서 3.5vh으로
      const vwSize = 5.6 + (5 - 5.6) * ratio; // 5.6vw에서 5vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else if (aspectRatio <= 0.27) {
      // 249:917 비율 이하 (매우 세로가 긴 경우) - 최대 크기
      return "min(5.7vmin, 4.8vh, 6.75vw)";
    } else {
      // 0.27 ~ 0.4 사이의 완만한 조정
      const ratio = (aspectRatio - 0.27) / (0.4 - 0.27);
      const vminSize = 5.7 + (4.7 - 5.7) * ratio; // 5.7vmin에서 4.7vmin으로
      const vhSize = 4.8 + (3.9 - 4.8) * ratio; // 4.8vh에서 3.9vh으로
      const vwSize = 6.75 + (5.6 - 6.75) * ratio; // 6.75vw에서 5.6vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    }
  }};

  font-family: Pretendard;

  font-weight: 600;
  line-height: ${({ orientation }) =>
    orientation === "portrait" ? "1.1" : "1.1"};
`;

const StyledFeelsLikeText = styled.span`
  color: white;

  font-size: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(3.2vmin, min(4.5vw, 4vh))";
    }
    // 세로형에서 비율에 따른 조정
    if (aspectRatio >= 0.675) {
      // 619:917 비율 이상 (가로가 더 긴 경우)
      return "min(5vmin, 4.2vh, 6vw)";
    } else if (aspectRatio >= 0.55) {
      // 619:917 ~ 505:917 비율 - 완만한 증가 시작
      const ratio = (aspectRatio - 0.55) / (0.675 - 0.55);
      const vminSize = 5.5 + (5 - 5.5) * ratio; // 5.5vmin에서 5vmin으로
      const vhSize = 4.6 + (4.2 - 4.6) * ratio; // 4.6vh에서 4.2vh으로
      const vwSize = 6.6 + (6 - 6.6) * ratio; // 6.6vw에서 6vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else if (aspectRatio >= 0.4) {
      // 505:917 ~ 367:917 비율 - 중간 단계
      const ratio = (aspectRatio - 0.4) / (0.55 - 0.4);
      const vminSize = 6.2 + (5.5 - 6.2) * ratio; // 6.2vmin에서 5.5vmin으로
      const vhSize = 5.2 + (4.6 - 5.2) * ratio; // 5.2vh에서 4.6vh으로
      const vwSize = 7.4 + (6.6 - 7.4) * ratio; // 7.4vw에서 6.6vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else if (aspectRatio <= 0.27) {
      // 249:917 비율 이하 (매우 세로가 긴 경우) - 최대 크기
      return "min(7.5vmin, 6.3vh, 9vw)";
    } else {
      // 0.27 ~ 0.4 사이의 완만한 조정
      const ratio = (aspectRatio - 0.27) / (0.4 - 0.27);
      const vminSize = 7.5 + (6.2 - 7.5) * ratio; // 7.5vmin에서 6.2vmin으로
      const vhSize = 6.3 + (5.2 - 6.3) * ratio; // 6.3vh에서 5.2vh으로
      const vwSize = 9 + (7.4 - 9) * ratio; // 9vw에서 7.4vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    }
  }};

  font-family: Pretendard;

  font-weight: 700;
  line-height: ${({ orientation }) =>
    orientation === "portrait" ? "1.2" : "1.1"};
  text-align: center;
`;

const StyledWeatherLabelText = styled.span`
  color: white;

  font-size: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(2.8vmin, min(3.5vw, 3.2vh))";
    }
    // 세로형에서 비율에 따른 조정
    if (aspectRatio >= 0.675) {
      // 619:917 비율 이상 (가로가 더 긴 경우)
      return "max(2.8vmin, min(3.5vw, 3.2vh))";
    } else if (aspectRatio >= 0.55) {
      // 619:917 ~ 505:917 비율 - 완만한 증가 시작
      const ratio = (aspectRatio - 0.55) / (0.675 - 0.55);
      const vminSize = 3.1 + (2.8 - 3.1) * ratio; // 3.1vmin에서 2.8vmin으로
      const vwSize = 3.8 + (3.5 - 3.8) * ratio; // 3.8vw에서 3.5vw으로
      const vhSize = 3.5 + (3.2 - 3.5) * ratio; // 3.5vh에서 3.2vh으로
      return `max(${vminSize}vmin, min(${vwSize}vw, ${vhSize}vh))`;
    } else if (aspectRatio >= 0.4) {
      // 505:917 ~ 367:917 비율 - 중간 단계
      const ratio = (aspectRatio - 0.4) / (0.55 - 0.4);
      const vminSize = 3.5 + (3.1 - 3.5) * ratio; // 3.5vmin에서 3.1vmin으로
      const vwSize = 4.3 + (3.8 - 4.3) * ratio; // 4.3vw에서 3.8vw으로
      const vhSize = 3.9 + (3.5 - 3.9) * ratio; // 3.9vh에서 3.5vh으로
      return `max(${vminSize}vmin, min(${vwSize}vw, ${vhSize}vh))`;
    } else if (aspectRatio <= 0.27) {
      // 249:917 비율 이하 (매우 세로가 긴 경우) - 최대 크기
      return "max(4.2vmin, min(5.25vw, 4.8vh))";
    } else {
      // 0.27 ~ 0.4 사이의 완만한 조정
      const ratio = (aspectRatio - 0.27) / (0.4 - 0.27);
      const vminSize = 4.2 + (3.5 - 4.2) * ratio; // 4.2vmin에서 3.5vmin으로
      const vwSize = 5.25 + (4.3 - 5.25) * ratio; // 5.25vw에서 4.3vw으로
      const vhSize = 4.8 + (3.9 - 4.8) * ratio; // 4.8vh에서 3.9vh으로
      return `max(${vminSize}vmin, min(${vwSize}vw, ${vhSize}vh))`;
    }
  }};
  font-family: Pretendard;

  font-weight: 700;
  line-height: ${({ orientation }) =>
    orientation === "portrait" ? "1.2" : "1.1"};
`;

const StyledWeatherValueText = styled.span`
  color: white;

  font-size: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(4vmin, min(6vw, 5vh))";
    }
    // 세로형에서 비율에 따른 조정
    if (aspectRatio >= 0.675) {
      // 619:917 비율 이상 (가로가 더 긴 경우)
      return "max(4vmin, min(6vw, 5vh))";
    } else if (aspectRatio >= 0.55) {
      // 619:917 ~ 505:917 비율 - 완만한 증가 시작
      const ratio = (aspectRatio - 0.55) / (0.675 - 0.55);
      const vminSize = 4.4 + (4 - 4.4) * ratio; // 4.4vmin에서 4vmin으로
      const vwSize = 6.6 + (6 - 6.6) * ratio; // 6.6vw에서 6vw으로
      const vhSize = 5.5 + (5 - 5.5) * ratio; // 5.5vh에서 5vh으로
      return `max(${vminSize}vmin, min(${vwSize}vw, ${vhSize}vh))`;
    } else if (aspectRatio >= 0.4) {
      // 505:917 ~ 367:917 비율 - 중간 단계
      const ratio = (aspectRatio - 0.4) / (0.55 - 0.4);
      const vminSize = 5 + (4.4 - 5) * ratio; // 5vmin에서 4.4vmin으로
      const vwSize = 7.5 + (6.6 - 7.5) * ratio; // 7.5vw에서 6.6vw으로
      const vhSize = 6.2 + (5.5 - 6.2) * ratio; // 6.2vh에서 5.5vh으로
      return `max(${vminSize}vmin, min(${vwSize}vw, ${vhSize}vh))`;
    } else if (aspectRatio <= 0.27) {
      // 249:917 비율 이하 (매우 세로가 긴 경우) - 최대 크기
      return "max(6vmin, min(9vw, 7.5vh))";
    } else {
      // 0.27 ~ 0.4 사이의 완만한 조정
      const ratio = (aspectRatio - 0.27) / (0.4 - 0.27);
      const vminSize = 6 + (5 - 6) * ratio; // 6vmin에서 5vmin으로
      const vwSize = 9 + (7.5 - 9) * ratio; // 9vw에서 7.5vw으로
      const vhSize = 7.5 + (6.2 - 7.5) * ratio; // 7.5vh에서 6.2vh으로
      return `max(${vminSize}vmin, min(${vwSize}vw, ${vhSize}vh))`;
    }
  }};
  font-family: Poppins;

  font-weight: 500;
  line-height: 1;
`;

const StyledWeatherUnitText = styled.span`
  color: white;

  font-size: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(2vmin, min(2.5vw, 2.2vh))";
    }
    // 세로형에서 비율에 따른 조정
    if (aspectRatio >= 0.675) {
      // 619:917 비율 이상 (가로가 더 긴 경우)
      return "max(2vmin, min(2.5vw, 2.2vh))";
    } else if (aspectRatio >= 0.55) {
      // 619:917 ~ 505:917 비율 - 완만한 증가 시작
      const ratio = (aspectRatio - 0.55) / (0.675 - 0.55);
      const vminSize = 2.2 + (2 - 2.2) * ratio; // 2.2vmin에서 2vmin으로
      const vwSize = 2.75 + (2.5 - 2.75) * ratio; // 2.75vw에서 2.5vw으로
      const vhSize = 2.4 + (2.2 - 2.4) * ratio; // 2.4vh에서 2.2vh으로
      return `max(${vminSize}vmin, min(${vwSize}vw, ${vhSize}vh))`;
    } else if (aspectRatio >= 0.4) {
      // 505:917 ~ 367:917 비율 - 중간 단계
      const ratio = (aspectRatio - 0.4) / (0.55 - 0.4);
      const vminSize = 2.5 + (2.2 - 2.5) * ratio; // 2.5vmin에서 2.2vmin으로
      const vwSize = 3.1 + (2.75 - 3.1) * ratio; // 3.1vw에서 2.75vw으로
      const vhSize = 2.7 + (2.4 - 2.7) * ratio; // 2.7vh에서 2.4vh으로
      return `max(${vminSize}vmin, min(${vwSize}vw, ${vhSize}vh))`;
    } else if (aspectRatio <= 0.27) {
      // 249:917 비율 이하 (매우 세로가 긴 경우) - 최대 크기
      return "max(3vmin, min(3.75vw, 3.3vh))";
    } else {
      // 0.27 ~ 0.4 사이의 완만한 조정
      const ratio = (aspectRatio - 0.27) / (0.4 - 0.27);
      const vminSize = 3 + (2.5 - 3) * ratio; // 3vmin에서 2.5vmin으로
      const vwSize = 3.75 + (3.1 - 3.75) * ratio; // 3.75vw에서 3.1vw으로
      const vhSize = 3.3 + (2.7 - 3.3) * ratio; // 3.3vh에서 2.7vh으로
      return `max(${vminSize}vmin, min(${vwSize}vw, ${vhSize}vh))`;
    }
  }};
  font-family: Poppins;

  font-weight: 700;
  line-height: ${({ orientation }) =>
    orientation === "portrait" ? "1.2" : "1.1"};
  letter-spacing: ${({ orientation }) =>
    orientation === "portrait" ? "0.1vmin" : "0.1vmin"};
`;

const StyledMessageText = styled.span`
  color: white;

  font-size: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(3.5vmin, min(4.5vw, 4vh))";
    }
    // 세로형에서 비율에 따른 조정
    // 문제: 249:917에서 폰트가 섹션에 비해 너무 작음
    // 해결: 249:917에서만 폰트 키우고, 다른 비율은 기존 크기 유지하되 자연스럽게 전환

    if (aspectRatio >= 1.15) {
      // 1055:917 비율 이상 - 기존 크기 유지
      return "min(4.2vmin, 3.5vh, 5.5vw)";
    } else if (aspectRatio >= 1.14) {
      // 1054:917 비율 - 기존 크기의 0.9배
      const scale = 0.9;
      const vminSize = 4.2 * scale; // 3.78vmin
      const vhSize = 3.5 * scale; // 3.15vh
      const vwSize = 5.5 * scale; // 4.95vw
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else if (aspectRatio >= 0.27) {
      // 1054:917 ~ 249:917 비율 사이 - 자연스러운 점진적 증가
      const ratio = (aspectRatio - 0.27) / (1.14 - 0.27);
      // 249:917에서 적절한 크기, 1054:917에서 0.9배 크기로 자연스럽게 연결
      const start249Vmin = 5.2; // 249:917에서 시작 크기
      const start249Vh = 4.4;
      const start249Vw = 6.8;
      const end1054Vmin = 3.78; // 1054:917에서 0.9배 크기
      const end1054Vh = 3.15;
      const end1054Vw = 4.95;
      const vminSize = start249Vmin + (end1054Vmin - start249Vmin) * ratio;
      const vhSize = start249Vh + (end1054Vh - start249Vh) * ratio;
      const vwSize = start249Vw + (end1054Vw - start249Vw) * ratio;
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else {
      // 249:917 비율 이하 - 적절한 폰트 크기
      return "min(5.2vmin, 4.4vh, 6.8vw)";
    }
  }};

  font-family: Pretendard;
  font-weight: 600;
  line-height: ${({ orientation }) =>
    orientation === "portrait" ? "1.2" : "1.2"};
  white-space: nowrap;
  overflow: visible;
  text-align: center;

  ${({ isOverflowing }) =>
    isOverflowing
      ? css`
          display: inline-block;
          will-change: transform;
          animation: ${marqueeAnimation} 15s linear infinite;
          position: relative;

          &::after {
            content: attr(data-text);
            position: absolute;
            left: 100%;
            top: 0;
            padding-left: 4em; /* Gap between repeated text */
            white-space: nowrap;
          }
        `
      : css`
          overflow: visible;
        `}
`;

const StyledDivider = styled.div`
  width: ${({ orientation }) =>
    orientation === "portrait"
      ? "max(0.4vmin, min(0.6vw, 0.5vh))"
      : "max(0.3vmin, min(0.4vw, 0.35vh))"};
  height: ${({ orientation }) =>
    orientation === "portrait"
      ? "max(2.5vmin, min(3.5vw, 3vh))"
      : "max(2vmin, min(2.8vw, 2.5vh))"};
  background: white;
  opacity: 0.6;
  border-radius: ${({ orientation }) =>
    orientation === "portrait"
      ? "max(0.2vmin, min(0.3vw, 0.25vh))"
      : "max(0.15vmin, min(0.2vw, 0.18vh))"};
  align-self: center;
`;

// Icon Components
const StyledHumidityIcon = styled.div`
  width: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(4vmin, min(5.5vw, 5vh))";
    }
    // 세로형에서 비율에 따른 조정
    if (aspectRatio >= 0.55) {
      // 505:917 비율 이상 (가로가 더 긴 경우)
      return "max(4vmin, min(5.5vw, 5vh))";
    } else if (aspectRatio <= 0.27) {
      // 249:917 비율 이하 (매우 세로가 긴 경우) - 1.5배 크기
      return "max(6vmin, min(8.25vw, 7.5vh))";
    } else {
      // 0.27 ~ 0.55 사이의 완만한 조정
      const ratio = (aspectRatio - 0.27) / (0.55 - 0.27);
      const vminSize = 6 + (4 - 6) * ratio; // 6vmin에서 4vmin으로
      const vwSize = 8.25 + (5.5 - 8.25) * ratio; // 8.25vw에서 5.5vw으로
      const vhSize = 7.5 + (5 - 7.5) * ratio; // 7.5vh에서 5vh으로
      return `max(${vminSize}vmin, min(${vwSize}vw, ${vhSize}vh))`;
    }
  }};
  height: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(4vmin, min(5.5vw, 5vh))";
    }
    // 세로형에서 비율에 따른 조정
    if (aspectRatio >= 0.55) {
      // 505:917 비율 이상 (가로가 더 긴 경우)
      return "max(4vmin, min(5.5vw, 5vh))";
    } else if (aspectRatio <= 0.27) {
      // 249:917 비율 이하 (매우 세로가 긴 경우) - 1.5배 크기
      return "max(6vmin, min(8.25vw, 7.5vh))";
    } else {
      // 0.27 ~ 0.55 사이의 완만한 조정
      const ratio = (aspectRatio - 0.27) / (0.55 - 0.27);
      const vminSize = 6 + (4 - 6) * ratio; // 6vmin에서 4vmin으로
      const vwSize = 8.25 + (5.5 - 8.25) * ratio; // 8.25vw에서 5.5vw으로
      const vhSize = 7.5 + (5 - 7.5) * ratio; // 7.5vh에서 5vh으로
      return `max(${vminSize}vmin, min(${vwSize}vw, ${vhSize}vh))`;
    }
  }};
  position: relative;
  background-image: url(${humidityIcon});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  flex-shrink: 0;
`;

const StyledWindSpeedIcon = styled.div`
  width: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(4vmin, min(5.5vw, 5vh))";
    }
    // 세로형에서 비율에 따른 조정
    if (aspectRatio >= 0.55) {
      // 505:917 비율 이상 (가로가 더 긴 경우)
      return "max(4vmin, min(5.5vw, 5vh))";
    } else if (aspectRatio <= 0.27) {
      // 249:917 비율 이하 (매우 세로가 긴 경우) - 1.5배 크기
      return "max(6vmin, min(8.25vw, 7.5vh))";
    } else {
      // 0.27 ~ 0.55 사이의 완만한 조정
      const ratio = (aspectRatio - 0.27) / (0.55 - 0.27);
      const vminSize = 6 + (4 - 6) * ratio; // 6vmin에서 4vmin으로
      const vwSize = 8.25 + (5.5 - 8.25) * ratio; // 8.25vw에서 5.5vw으로
      const vhSize = 7.5 + (5 - 7.5) * ratio; // 7.5vh에서 5vh으로
      return `max(${vminSize}vmin, min(${vwSize}vw, ${vhSize}vh))`;
    }
  }};
  height: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(4vmin, min(5.5vw, 5vh))";
    }
    // 세로형에서 비율에 따른 조정
    if (aspectRatio >= 0.55) {
      // 505:917 비율 이상 (가로가 더 긴 경우)
      return "max(4vmin, min(5.5vw, 5vh))";
    } else if (aspectRatio <= 0.27) {
      // 249:917 비율 이하 (매우 세로가 긴 경우) - 1.5배 크기
      return "max(6vmin, min(8.25vw, 7.5vh))";
    } else {
      // 0.27 ~ 0.55 사이의 완만한 조정
      const ratio = (aspectRatio - 0.27) / (0.55 - 0.27);
      const vminSize = 6 + (4 - 6) * ratio; // 6vmin에서 4vmin으로
      const vwSize = 8.25 + (5.5 - 8.25) * ratio; // 8.25vw에서 5.5vw으로
      const vhSize = 7.5 + (5 - 7.5) * ratio; // 7.5vh에서 5vh으로
      return `max(${vminSize}vmin, min(${vwSize}vw, ${vhSize}vh))`;
    }
  }};
  position: relative;
  background-image: url(${windspeedIcon});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  flex-shrink: 0;
`;

const StyledRainIcon = styled.div`
  width: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(4vmin, min(5.5vw, 5vh))";
    }
    // 세로형에서 비율에 따른 조정
    if (aspectRatio >= 0.55) {
      // 505:917 비율 이상 (가로가 더 긴 경우)
      return "max(4vmin, min(5.5vw, 5vh))";
    } else if (aspectRatio <= 0.27) {
      // 249:917 비율 이하 (매우 세로가 긴 경우) - 1.5배 크기
      return "max(6vmin, min(8.25vw, 7.5vh))";
    } else {
      // 0.27 ~ 0.55 사이의 완만한 조정
      const ratio = (aspectRatio - 0.27) / (0.55 - 0.27);
      const vminSize = 6 + (4 - 6) * ratio; // 6vmin에서 4vmin으로
      const vwSize = 8.25 + (5.5 - 8.25) * ratio; // 8.25vw에서 5.5vw으로
      const vhSize = 7.5 + (5 - 7.5) * ratio; // 7.5vh에서 5vh으로
      return `max(${vminSize}vmin, min(${vwSize}vw, ${vhSize}vh))`;
    }
  }};
  height: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(4vmin, min(5.5vw, 5vh))";
    }
    // 세로형에서 비율에 따른 조정
    if (aspectRatio >= 0.55) {
      // 505:917 비율 이상 (가로가 더 긴 경우)
      return "max(4vmin, min(5.5vw, 5vh))";
    } else if (aspectRatio <= 0.27) {
      // 249:917 비율 이하 (매우 세로가 긴 경우) - 1.5배 크기
      return "max(6vmin, min(8.25vw, 7.5vh))";
    } else {
      // 0.27 ~ 0.55 사이의 완만한 조정
      const ratio = (aspectRatio - 0.27) / (0.55 - 0.27);
      const vminSize = 6 + (4 - 6) * ratio; // 6vmin에서 4vmin으로
      const vwSize = 8.25 + (5.5 - 8.25) * ratio; // 8.25vw에서 5.5vw으로
      const vhSize = 7.5 + (5 - 7.5) * ratio; // 7.5vh에서 5vh으로
      return `max(${vminSize}vmin, min(${vwSize}vw, ${vhSize}vh))`;
    }
  }};
  position: relative;
  background-image: url(${precipitationIcon});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  flex-shrink: 0;
`;

// Layout Components
const StyledMainContainer = styled.div`
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  background: #272727;
  overflow: hidden;
  display: flex;
  box-sizing: border-box;
  z-index: 1000;
`;

const StyledContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  ${({ orientation }) => {
    if (orientation === "landscape") {
      return `
        flex-direction: column;
        justify-content: flex-start;
  align-items: stretch;
      `;
    } else {
      return `
        flex-direction: column;
        justify-content: flex-start;
        align-items: stretch;
      `;
    }
  }}
  display: flex;
  box-sizing: border-box;
  gap: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "1vmin";
    }
    // 세로형에서 비율에 따른 간격 조정 - 세부정보와 하단 텍스트 사이 간격 대폭 증가
    if (aspectRatio >= 1.15) {
      // 1055:917 비율 이상 - 기본 간격
      return "0.8vmin";
    } else if (aspectRatio >= 0.85) {
      // 1054:917 ~ 780:917 비율 사이 - 간격 대폭 증가
      const ratio = (aspectRatio - 0.85) / (1.15 - 0.85);
      const gapSize = 3.0 + (0.8 - 3.0) * ratio; // 3.0vmin에서 0.8vmin으로
      return `${gapSize}vmin`;
    } else if (aspectRatio >= 0.27) {
      // 780:917 ~ 249:917 비율 사이 - 간격 3배 더 증가로 자연스럽게 점진적 증가
      const ratio = (aspectRatio - 0.27) / (0.85 - 0.27);
      const gapSize = 13.5 + (3.0 - 13.5) * ratio; // 13.5vmin(4.5×3)에서 3.0vmin으로
      return `${gapSize}vmin`;
    } else {
      // 249:917 비율 이하 - 최대 간격 (3배 증가)
      return "13.5vmin";
    }
  }};
  overflow: hidden;
  padding: ${({ orientation, aspectRatio }) => {
    console.log(
      "🚀 StyledContentWrapper padding - orientation:",
      orientation,
      "aspectRatio:",
      aspectRatio
    );

    if (orientation === "landscape") {
      // 가로 비율에 따른 자연스러운 패딩 변화 - 1055:917에서 1376:917까지 자연스럽게 연결
      if (aspectRatio >= 1.5) {
        // 1376:917 비율 이상 - 최대 패딩 (사이즈 영역 높이만큼 추가)
        return "5.5vmin 12vmin 6vmin 12vmin"; // 위쪽 패딩 3vmin + 2.5vmin
      } else if (aspectRatio >= 1.15) {
        // 1055:917 ~ 1376:917 비율 사이 - 자연스러운 점진적 증가
        const ratio = (aspectRatio - 1.15) / (1.5 - 1.15); // 1055:917(0) ~ 1376:917(1)
        const leftRightPadding = 2.4 + (12 - 2.4) * ratio; // 2.4vmin(12/5)에서 12vmin으로
        return `5.5vmin ${leftRightPadding}vmin 6vmin ${leftRightPadding}vmin`; // 위쪽 패딩 3vmin + 2.5vmin
      } else {
        // 1055:917 비율 이하 - 기본 패딩 (사이즈 영역 높이만큼 추가)
        return "5.5vmin 2.4vmin 6vmin 2.4vmin"; // 위쪽 패딩 3vmin + 2.5vmin
      }
    }

    // 세로형에서 비율에 따른 좌우 패딩 - 1055:917 비율에서 좌우 패딩 대폭 증가 (사이즈 영역 높이만큼 추가)
    if (aspectRatio >= 1.15) {
      console.log("🚀 Applying 1055:917+ padding: 5.5vmin 12vmin");
      return "5.5vmin 12vmin"; // 위쪽 패딩 3vmin + 2.5vmin
    } else if (aspectRatio >= 0.27) {
      const ratio = (aspectRatio - 0.27) / (1.15 - 0.27);
      const topBottomPadding = 2 + (3 - 2) * ratio;
      const adjustedTopPadding = topBottomPadding + 2.5; // 사이즈 영역 높이만큼 추가
      const leftRightPadding = 12 + (12 - 12) * ratio;
      console.log(
        "🚀 Applying middle range padding:",
        `${adjustedTopPadding}vmin ${leftRightPadding}vmin`
      );
      return `${adjustedTopPadding}vmin ${leftRightPadding}vmin`;
    } else {
      console.log("🚀 Applying 249:917- padding: 4.5vmin 12vmin");
      return "4.5vmin 12vmin"; // 위쪽 패딩 2vmin + 2.5vmin
    }
  }};
`;

// 세로형일 때 - 메인 콘텐츠 영역 (타이틀 + 날씨메인 + 상세)
const StyledMainContentArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  gap: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "1vmin";
    }
    // 세로형에서 비율에 따른 간격 조정
    if (aspectRatio >= 1.15) {
      // 1055:917 비율 이상 - 기본 간격
      return "1.5vmin";
    } else if (aspectRatio >= 0.27) {
      // 1054:917 ~ 249:917 비율 사이 - 간격 점진적 감소
      const ratio = (aspectRatio - 0.27) / (1.15 - 0.27);
      const gapSize = 0.8 + (1.5 - 0.8) * ratio; // 0.8vmin에서 1.5vmin으로
      return `${gapSize}vmin`;
    } else {
      // 249:917 비율 이하 - 최소 간격
      return "0.8vmin";
    }
  }};

  overflow: visible;
  ${({ orientation }) => {
    if (orientation === "landscape") {
      return `
    width: 100%;
    flex-direction: row;
        justify-content: flex-start;
        align-items: stretch;
      `;
    } else {
      return `justify-content: flex-start;`;
    }
  }}
`;

// 섹션 1: 타이틀 섹션 (고성군, 시간, 날짜)
const StyledTitleSection = styled.div`
  width: 100%;
  ${({ orientation }) =>
    orientation === "portrait"
      ? `
        flex: 1.2;
        min-height: 0;
      `
      : `
        flex: 1;
        min-width: 25%;
        max-width: 35%;
        align-self: flex-start;
        margin-top: 14vmin;
      `}
  flex-direction: column;
  justify-content: ${({ orientation }) =>
    orientation === "portrait" ? "center" : "flex-start"};
  align-items: ${({ orientation }) =>
    orientation === "portrait" ? "center" : "flex-start"};
  text-align: ${({ orientation }) =>
    orientation === "landscape" ? "left" : "center"};
  gap: ${({ orientation, aspectRatio }) => {
    if (orientation === "portrait") {
      if (aspectRatio <= 0.39) {
        // 249:917~360:917 비율에서 지역명과 시간/날짜 사이 간격 3배에서 4.5배로 증가 (1.5배 더)
        return "min(9vmin, 7.2vh)"; // 기존 6vmin × 1.5 = 9vmin
      }
      return "min(2vmin, 1.6vh)";
    }
    return "2vmin";
  }};
  display: flex;
  overflow: hidden;
`;

const StyledDateTimeSection = styled.div`
  flex-direction: column;
  justify-content: center;
  align-items: ${({ orientation }) =>
    orientation === "landscape" ? "flex-start" : "center"};
  text-align: ${({ orientation }) =>
    orientation === "landscape" ? "left" : "center"};
  gap: ${({ orientation, aspectRatio }) => {
    if (orientation === "portrait") {
      if (aspectRatio <= 0.39) {
        // 249:917~360:917 비율에서 시간과 날짜 사이 간격 2배 증가
        return "2vmin"; // 기존 1vmin × 2 = 2vmin
      }
      return "1vmin";
    }
    return "0.8vmin";
  }};
  display: flex;
`;

// 섹션 2: 날씨 메인 섹션 (아이콘, 온도, 날씨상태, 최저/최고, 체감온도)
const StyledWeatherMainSection = styled.div`
  width: 100%;
  ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      // 가로 모드에서 나머지 공간을 모두 차지하도록 설정
      return `
        flex: 1;
        min-width: 0;
        width: auto;
        margin-right: 2vmin;
      `;
    }
    // 세로형에서 비율에 따른 조정 - 세부 정보 섹션과 균형 맞추기
    if (aspectRatio >= 1.15) {
      // 1055:917 비율 이상 - 세부 정보 섹션과 균형
      return `
        flex: 1.5;
        min-height: 0;
      `;
    } else if (aspectRatio >= 1.14) {
      // 1054:917 비율 - 세부 정보 섹션과 균형
      return `
        flex: 1.2;
        min-height: 0;
      `;
    } else if (aspectRatio >= 0.27) {
      // 249:917 ~ 1054:917 비율 사이 - 세부 정보 섹션과 균형
      const ratio = (aspectRatio - 0.27) / (1.14 - 0.27);
      // flex 크기를 조정하여 세부 정보 섹션과 균형
      const flexSize = 1.8 + (1.2 - 1.8) * ratio; // 1.8에서 1.2로 감소
      const translateY = -2.0 + (0 - -2.0) * ratio; // -2vh에서 0vh로
      return `
        flex: ${flexSize};
        min-height: 0;
        transform: translateY(${translateY}vh);
        margin-bottom: ${translateY}vh;
      `;
    } else {
      // 249:917 비율 이하 - 세부 정보 섹션과 균형
      return `
        flex: 1.8;
        min-height: 0;
        transform: translateY(-2vh);
        margin-bottom: -2vh;
      `;
    }
  }}
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ orientation, aspectRatio }) => {
    if (orientation === "portrait") {
      if (aspectRatio <= 0.39) {
        // 249:917~360:917 비율에서 아이콘과 온도영역 사이 간격 5배 증가
        return "min(11.25vmin, 7.5vh)"; // 기존 2.25vmin × 5 = 11.25vmin
      }
      return "min(2.25vmin, 1.5vh)";
    }
    return "2.25vmin";
  }};
  display: flex;
  overflow: hidden;
`;

const StyledWeatherIconSection = styled.div`
  justify-content: center;
  align-items: center;
  gap: ${({ orientation }) =>
    orientation === "portrait" ? "2vmin" : "1.5vmin"};
  display: flex;
  width: 100%;
  /* flex: 1; */
`;

const StyledTemperatureSection = styled.div`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ orientation }) =>
    orientation === "portrait" ? "1.5vmin" : "1vmin"};
  display: flex;
`;

const StyledTempInfoSection = styled.div`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ orientation, aspectRatio }) => {
    if (orientation === "portrait") {
      if (aspectRatio <= 0.27) {
        // 249:917 비율 이하 - 기본 간격의 0.7배
        return "1.75vmin"; // 2.5vmin × 0.7 = 1.75vmin
      } else if (aspectRatio >= 0.27 && aspectRatio <= 0.39) {
        // 249:917~357:917 비율에서 점진적으로 간격 조정 (0.7배에서 2.5배로)
        const ratio = (aspectRatio - 0.27) / (0.39 - 0.27);
        const startGap = 1.75; // 249:917에서 0.7배 간격
        const endGap = 2.5; // 357:917에서 2.5배 간격
        const gapSize = startGap + (endGap - startGap) * ratio;
        return `${gapSize}vmin`;
      } else if (aspectRatio >= 0.39 && aspectRatio <= 0.72) {
        // 358:917~660:917 비율에서 간격 2배 증가
        return "2vmin"; // 기존 1vmin × 2 = 2vmin
      }
      return "1vmin";
    }
    return "1vmin";
  }};
  display: flex;
  width: 100%;
`;

const StyledMinMaxSection = styled.div`
  opacity: 0.8;
  justify-content: center;
  align-items: center;
  gap: ${({ orientation }) =>
    orientation === "portrait" ? "2.5vmin" : "1.5vmin"};
  display: flex;
`;

// 섹션 3: 날씨 상세 섹션 (습도, 풍속, 강수량)
const StyledWeatherDetailsSection = styled.div`
  width: 100%;
  ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return `
        flex: 0 0 auto;
        width: 320px;
        min-width: 280px;
        max-width: 360px;
        height: 60vh;
        margin-top: 14vmin;
        margin-bottom: auto;
        box-sizing: border-box;
        overflow: visible;
      `;
    }
    // 세로형에서 비율에 따른 조정 - 고정 크기로 잘림 방지
    if (aspectRatio >= 1.15) {
      // 1055:917 비율 이상 - 고정 크기
      return `
        flex: 0 0 auto;
        height: auto;
        max-height: none;
        min-height: 0;
      `;
    } else if (aspectRatio >= 1.14) {
      // 1054:917 비율 - 고정 크기
      return `
        flex: 0 0 auto;
        height: auto;
        max-height: none;
        min-height: 0;
        transform: translateY(-0.8vh);
        margin-bottom: 0.8vh;
      `;
    } else if (aspectRatio >= 0.27) {
      // 249:917 ~ 1054:917 비율 사이 - 고정 크기
      const ratio = (aspectRatio - 0.27) / (1.14 - 0.27);

      // Y 위치: 249:917(-0.7vh) → 1054:917(-0.8vh) 살짝 위로 이동
      const translateY = -0.7 + (-0.8 - -0.7) * ratio;

      // 하단 마진: 249:917(2.8vh) → 1054:917(0.8vh) 점진적 감소
      const marginBottom = 2.8 + (0.8 - 2.8) * ratio;

      return `
        flex: 0 0 auto;
        height: auto;
        max-height: none;
        min-height: 0;
        transform: translateY(${translateY}vh);
        margin-bottom: ${marginBottom}vh;
      `;
    } else {
      // 249:917 비율 이하 - 고정 크기
      return `
        flex: 0 0 auto;
        height: auto;
        max-height: none;
        min-height: 0;
        transform: translateY(-0.7vh);
        margin-bottom: 2.8vh;
      `;
    }
  }}
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "1.0vmin";
    }
    // 세로형에서 비율에 따른 간격 조정 - 간격 줄여서 잘림 방지
    if (aspectRatio >= 1.15) {
      // 1055:917 비율 이상 - 간격 줄이기
      return "0.8vmin";
    } else if (aspectRatio >= 0.65) {
      // 1054:917 ~ 600:917 비율 사이 - 간격 줄이기
      return "1.0vmin";
    } else if (aspectRatio >= 0.27) {
      // 600:917 ~ 249:917 비율 사이 - 간격 적절히 조정
      const ratio = (aspectRatio - 0.27) / (0.65 - 0.27);
      const gapSize = 1.5 + (1.0 - 1.5) * ratio; // 1.5vmin에서 1.0vmin으로
      return `${gapSize}vmin`;
    } else {
      // 249:917 비율 이하 - 적정 간격
      return "1.5vmin";
    }
  }};
  display: flex;
  overflow: visible;
  box-sizing: border-box;
`;

const StyledWeatherDetailCard = styled.div`
  padding: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "1.0vmin 1.2vmin";
    }
    // 세로형에서 비율에 따른 조정 - 패딩 최소화하여 잘림 방지
    if (aspectRatio >= 1.15) {
      // 1055:917 비율 이상 - 패딩 최소화
      return "1.5vmin";
    } else if (aspectRatio >= 1.14) {
      // 1054:917 비율 - 패딩 최소화
      return "1.0vmin";
    } else if (aspectRatio >= 0.27) {
      // 1054:917 ~ 249:917 비율 사이 - 패딩 최소화
      const ratio = (aspectRatio - 0.27) / (1.14 - 0.27);
      const paddingSize = 1.8 + (1.0 - 1.8) * ratio; // 1.8vmin에서 1.0vmin으로
      return `${paddingSize}vmin`;
    } else {
      // 249:917 비율 이하 - 패딩 최소화
      return "1.8vmin";
    }
  }};
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "0.6vmin";
    }
    // 세로형에서 비율에 따른 조정 - 패딩에 맞춰 모서리 반경 조정
    if (aspectRatio >= 1.15) {
      // 1055:917 비율 이상 - 패딩에 맞춰 조정
      return "0.8vmin";
    } else if (aspectRatio >= 1.14) {
      // 1054:917 비율 - 패딩에 맞춰 조정
      return "0.6vmin";
    } else if (aspectRatio >= 0.27) {
      // 1054:917 ~ 249:917 비율 사이 - 패딩에 맞춰 점진적 조정
      const ratio = (aspectRatio - 0.27) / (1.14 - 0.27);
      const radiusSize = 1.0 + (0.6 - 1.0) * ratio; // 1.0vmin에서 0.6vmin으로
      return `${radiusSize}vmin`;
    } else {
      // 249:917 비율 이하 - 패딩에 맞춰 조정
      return "1.0vmin";
    }
  }};
  justify-content: space-between;
  align-items: center;
  display: flex;
  flex: 1;
  box-sizing: border-box;
`;

const StyledDetailIconSection = styled.div`
  justify-content: flex-start;
  align-items: center;
  gap: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "0.8vmin";
    }
    // 세로형에서 비율에 따른 조정 - 간격 최소화하여 잘림 방지
    if (aspectRatio >= 1.15) {
      // 1055:917 비율 이상 - 간격 최소화
      return "1.2vmin";
    } else if (aspectRatio >= 1.14) {
      // 1054:917 비율 - 간격 최소화
      return "0.8vmin";
    } else if (aspectRatio >= 0.27) {
      // 1054:917 ~ 249:917 비율 사이 - 간격 최소화
      const ratio = (aspectRatio - 0.27) / (1.14 - 0.27);
      const gapSize = 1.5 + (0.8 - 1.5) * ratio; // 1.5vmin에서 0.8vmin으로
      return `${gapSize}vmin`;
    } else {
      // 249:917 비율 이하 - 간격 최소화
      return "1.5vmin";
    }
  }};
  display: flex;
`;

const StyledDetailValueSection = styled.div`
  justify-content: flex-end;
  align-items: flex-end;
  gap: ${({ orientation }) =>
    orientation === "portrait" ? "0.3vmin" : "0.3vmin"};
  display: flex;
`;

const StyledWeatherIcon = styled.div`
  width: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(15vmin, min(20vw, 18vh))";
    }
    // 세로형에서 비율에 따른 조정
    if (aspectRatio >= 1.15) {
      // 1054:917 비율 이상 (가로가 더 긴 경우) - 1.2배 크기
      return "min(24vmin, 18vh, 18vw)";
    } else if (aspectRatio >= 0.55) {
      // 1054:917 ~ 505:917 비율 사이 - 완만한 조정
      const ratio = (aspectRatio - 0.55) / (1.15 - 0.55);
      const vminSize = 20 + (24 - 20) * ratio; // 20vmin에서 24vmin으로
      const vhSize = 15 + (18 - 15) * ratio; // 15vh에서 18vh으로
      const vwSize = 15 + (18 - 15) * ratio; // 15vw에서 18vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else if (aspectRatio <= 0.27) {
      // 249:917 비율 이하 (매우 세로가 긴 경우) - 기존 1.5배에서 추가로 1.5배 더 = 2.25배
      return "min(45vmin, 33.75vh, 33.75vw)"; // 30vmin × 1.5 = 45vmin
    } else if (aspectRatio <= 0.39) {
      // 249:917~360:917 비율에서 아이콘 크기 1.5배 더 증가
      const ratio = (aspectRatio - 0.27) / (0.39 - 0.27);
      const baseVmin = 45; // 249:917에서의 기본값 (30 × 1.5)
      const baseVh = 33.75;
      const baseVw = 33.75;
      const targetVmin = 30; // 360:917에서의 목표값 (20 × 1.5)
      const targetVh = 22.5;
      const targetVw = 22.5;
      const vminSize = baseVmin + (targetVmin - baseVmin) * ratio;
      const vhSize = baseVh + (targetVh - baseVh) * ratio;
      const vwSize = baseVw + (targetVw - baseVw) * ratio;
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else {
      // 0.39 ~ 0.55 사이의 완만한 조정
      const ratio = (aspectRatio - 0.39) / (0.55 - 0.39);
      const vminSize = 30 + (20 - 30) * ratio; // 30vmin에서 20vmin으로
      const vhSize = 22.5 + (15 - 22.5) * ratio; // 22.5vh에서 15vh으로
      const vwSize = 22.5 + (15 - 22.5) * ratio; // 22.5vw에서 15vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    }
  }};
  height: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return "max(15vmin, min(20vw, 18vh))";
    }
    // 세로형에서 비율에 따른 조정 (width와 동일한 로직)
    if (aspectRatio >= 1.15) {
      // 1054:917 비율 이상 (가로가 더 긴 경우) - 1.2배 크기
      return "min(24vmin, 18vh, 18vw)";
    } else if (aspectRatio >= 0.55) {
      // 1054:917 ~ 505:917 비율 사이 - 완만한 조정
      const ratio = (aspectRatio - 0.55) / (1.15 - 0.55);
      const vminSize = 20 + (24 - 20) * ratio; // 20vmin에서 24vmin으로
      const vhSize = 15 + (18 - 15) * ratio; // 15vh에서 18vh으로
      const vwSize = 15 + (18 - 15) * ratio; // 15vw에서 18vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else if (aspectRatio <= 0.27) {
      // 249:917 비율 이하 (매우 세로가 긴 경우) - 기존 1.5배에서 추가로 1.5배 더 = 2.25배
      return "min(45vmin, 33.75vh, 33.75vw)"; // 30vmin × 1.5 = 45vmin
    } else if (aspectRatio <= 0.39) {
      // 249:917~360:917 비율에서 아이콘 크기 1.5배 더 증가
      const ratio = (aspectRatio - 0.27) / (0.39 - 0.27);
      const baseVmin = 45; // 249:917에서의 기본값 (30 × 1.5)
      const baseVh = 33.75;
      const baseVw = 33.75;
      const targetVmin = 30; // 360:917에서의 목표값 (20 × 1.5)
      const targetVh = 22.5;
      const targetVw = 22.5;
      const vminSize = baseVmin + (targetVmin - baseVmin) * ratio;
      const vhSize = baseVh + (targetVh - baseVh) * ratio;
      const vwSize = baseVw + (targetVw - baseVw) * ratio;
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    } else {
      // 0.39 ~ 0.55 사이의 완만한 조정
      const ratio = (aspectRatio - 0.39) / (0.55 - 0.39);
      const vminSize = 30 + (20 - 30) * ratio; // 30vmin에서 20vmin으로
      const vhSize = 22.5 + (15 - 22.5) * ratio; // 22.5vh에서 15vh으로
      const vwSize = 22.5 + (15 - 22.5) * ratio; // 22.5vw에서 15vw으로
      return `min(${vminSize}vmin, ${vhSize}vh, ${vwSize}vw)`;
    }
  }};
  position: relative;
  background-image: url(${weatherIcon});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  flex-shrink: 0;
`;

// 섹션 4: 하단 메시지 섹션
const StyledBottomMessage = styled.div`
  width: 100%;
  ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      return `
        position: absolute;
        bottom: 3vmin;
        left: 3vmin;
        right: 3vmin;
        height: 6vmin;
      `;
    }
    // 세로형에서 비율에 따른 조정 - 하단에 고정되도록 설정
    if (aspectRatio >= 1.15) {
      // 1055:917 비율 이상 - 적절한 크기 유지
      return `
        flex: 0 0 5vh;
        min-height: 0;
        margin-top: auto;
      `;
    } else if (aspectRatio >= 1.14) {
      // 1054:917 비율 - 1055:917과 자연스럽게 연결
      const ratio = (aspectRatio - 1.14) / (1.15 - 1.14);
      const flexSize = 4.7 + (5 - 4.7) * ratio; // 4.7vh에서 5vh으로 자연스럽게
      return `
        flex: 0 0 ${flexSize}vh;
        min-height: 0;
        margin-top: auto;
      `;
    } else if (aspectRatio >= 0.39) {
      // 1054:917 ~ 360:917 비율 사이 - 자연스러운 점진적 조정
      const ratio = (aspectRatio - 0.39) / (1.14 - 0.39);
      const flexSize = 4.0 + (4.7 - 4.0) * ratio; // 4.0vh에서 4.7vh으로
      return `
        flex: 0 0 ${flexSize}vh;
        min-height: 0;
        margin-top: auto;
      `;
    } else if (aspectRatio >= 0.27) {
      // 360:917 ~ 249:917 비율 사이 - 1.2배 크기 적용
      const ratio = (aspectRatio - 0.27) / (0.39 - 0.27);
      const baseSize = 2.8; // 원래 크기
      const enlargedSize = baseSize * 1.2; // 1.2배 크기 = 3.36vh
      const flexSize = enlargedSize + (4.0 - enlargedSize) * ratio; // 3.36vh에서 4.0vh으로
      return `
        flex: 0 0 ${flexSize}vh;
        min-height: 0;
        margin-top: auto;
      `;
    } else {
      // 249:917 비율 이하 - 1.2배 크기 적용
      const baseSize = 2.8; // 원래 크기
      const enlargedSize = baseSize * 1.2; // 1.2배 크기 = 3.36vh
      return `
        flex: 0 0 ${enlargedSize}vh;
        min-height: 0;
        margin-top: auto;
      `;
    }
  }}
  padding: ${({ orientation, aspectRatio }) => {
    if (orientation === "landscape") {
      // 가로 비율에서 메인 컨테이너 패딩과 동일한 좌우 여백 적용
      if (aspectRatio >= 1.5) {
        // 1376:917 비율 이상 - 메인 컨테이너 패딩과 동일 (12vmin)
        return "1vmin 0vmin"; // 좌우 패딩 제거하여 메인 컨테이너 패딩이 적용되도록
      } else if (aspectRatio >= 1.15) {
        // 1055:917 ~ 1376:917 비율 사이 - 메인 컨테이너 패딩과 동일하게 점진적 변화
        return "1vmin 0vmin"; // 좌우 패딩 제거하여 메인 컨테이너 패딩이 적용되도록
      } else {
        // 1055:917 비율 이하 - 메인 컨테이너 패딩과 동일 (2.4vmin)
        return "1vmin 0vmin"; // 좌우 패딩 제거하여 메인 컨테이너 패딩이 적용되도록
      }
    }
    // 세로형에서 비율에 따른 조정 - 좌우 여백 추가
    if (aspectRatio >= 1.15) {
      // 1055:917 비율 이상 - 좌우 여백 추가
      return "1vmin 3vmin"; // 좌우 3vmin 여백 추가
    } else if (aspectRatio >= 1.14) {
      // 1054:917 비율 - 좌우 여백 추가
      return "1.2vmin 3vmin 0.4vmin 3vmin"; // 좌우 3vmin 여백 추가
    } else if (aspectRatio >= 0.67) {
      // 1054:917 ~ 612:917 비율 사이 - 좌우 여백 추가
      return "1.2vmin 3vmin 0.4vmin 3vmin"; // 좌우 3vmin 여백 추가
    } else if (aspectRatio >= 0.27) {
      // 612:917 ~ 249:917 비율 사이 - 좌우 여백 추가
      return "1.2vmin 3vmin 0.4vmin 3vmin"; // 좌우 3vmin 여백 추가
    } else {
      // 249:917 비율 이하 - 좌우 여백 추가
      return "1.2vmin 3vmin 0.4vmin 3vmin"; // 좌우 3vmin 여백 추가
    }
  }};
  border-top: ${({ orientation }) =>
      orientation === "portrait" ? "min(0.2vmin, 0.15vh)" : "0.15vmin"}
    rgba(255, 255, 255, 0.4) solid;
  border-bottom: ${({ orientation }) =>
      orientation === "portrait" ? "min(0.2vmin, 0.15vh)" : "0.15vmin"}
    rgba(255, 255, 255, 0.4) solid;
  backdrop-filter: blur(
    ${({ orientation }) =>
      orientation === "portrait" ? "min(0.3vmin, 0.25vh)" : "0.2vmin"}
  );
  justify-content: center;
  align-items: center;
  display: flex;
  ${({ orientation, aspectRatio }) => {
    // Y축 기준 중앙 배치를 위한 조정 - 비율별 세밀한 유동적 조정
    if (orientation === "portrait" && aspectRatio >= 1.15) {
      // 1055:917 이상 - 아래로 살짝 이동
      return `
        transform: translateY(0.5vh);
      `;
    } else if (
      orientation === "portrait" &&
      aspectRatio >= 1.14 &&
      aspectRatio < 1.15
    ) {
      // 1054:917 비율 - 위로 살짝만 이동 (위쪽과 맞닿지 않게)
      return `
        transform: translateY(-0.4vh);
      `;
    } else if (
      orientation === "portrait" &&
      aspectRatio >= 0.27 &&
      aspectRatio < 1.14
    ) {
      // 1054:917 ~ 249:917 비율 사이 - 자연스럽고 완만한 점진적 조정
      const ratio = (aspectRatio - 0.27) / (1.14 - 0.27);
      const translateY = -1.2 + (-0.4 - -1.2) * ratio; // -1.2vh에서 -0.4vh로 완만하게
      return `
        transform: translateY(${translateY}vh);
      `;
    }
    return "";
  }}
  overflow: hidden;
  white-space: nowrap;
  box-sizing: border-box;
`;

// 249:917~360:917 비율에서 아이콘만 표시하는 섹션
const StyledWeatherIconOnlySection = styled.div`
  width: 100%;
  justify-content: center;
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  margin-bottom: ${({ aspectRatio }) => {
    if (aspectRatio <= 0.39) {
      return "min(5vmin, 3.5vh)"; // 아이콘과 온도 영역 사이 간격
    }
    return "0";
  }};
  ${({ aspectRatio }) => {
    if (aspectRatio <= 0.39) {
      // 아이콘을 위로 이동
      return `
        transform: translateY(-1.5vh);
      `;
    }
    return "";
  }}
`;

// 249:917~360:917 비율에서 온도 정보만 표시하는 섹션
const StyledTemperatureOnlySection = styled.div`
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ aspectRatio }) => {
    if (aspectRatio <= 0.39) {
      return "2.5vmin"; // 온도 정보들 사이 간격 2.5배 증가
    }
    return "1vmin";
  }};
  display: flex;
  flex: 0 0 auto;
  ${({ aspectRatio }) => {
    if (aspectRatio <= 0.39) {
      // 온도 영역을 아이콘 이동 높이의 절반만큼 위로 이동
      return `
        transform: translateY(-0.75vh);
      `;
    }
    return "";
  }}
`;

// styled-components 추가
const Header = styled.div`
  width: 100%;
  height: 56px;
  background: #fff;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 0 32px;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 100;
`;

const TitleContainer = styled.div`
  display: flex;
  position: absolute;
  align-items: center;
  gap: 16px;
  left: 50%;
  transform: translateX(-50%);
`;

const TemplateCategory = styled.div`
  color: ${palette.primary};
  font-weight: 500;
  background-color: ${palette.primaryLightest};
  padding: 3px 8px;
  border-radius: 2px;
`;

const HeaderTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #222;
`;

// 플로팅 버튼 컨테이너
const StyledFloatingButtonContainer = styled.div`
  position: fixed;
  bottom: 5%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
  display: flex;
  justify-content: center;
  align-items: center;
`;

// 사이즈 영역 컨테이너
const StyledSizeAreaContainer = styled.div`
  position: absolute;
  top: 56px;
  left: 0;
  right: 0;
  z-index: 99;
`;

// const Header = styled.div`
//   position: relative;
//   display: flex;
//   align-items: center;
//   justify-content: space-between;
//   padding: 7px 14px;
//   background: white;
//   border-bottom: 1px solid #e0e0e0;
//   flex-shrink: 0; // 헤더는 크기가 줄어들지 않도록 설정
// `;

// const TitleContainer = styled.div`
//   position: absolute;
//   left: 50%;
//   transform: translateX(-50%);
//   display: flex;
//   align-items: center;
//   gap: 12px;
// `;

// const HeaderTitle = styled.div`
//   font-size: 18px;
//   font-weight: 600;
//   color: ${palette.gray800};
// `;

// const TemplateCategory = styled.div`
//   color: ${palette.primary};
//   font-weight: 500;
//   background-color: ${palette.primaryLightest};
//   padding: 3px 8px;
//   border-radius: 2px;
// `;
